import { RESULT_TYPES } from 'src/data/result-types.ts';
import { Answers, ResultType, Section } from 'src/types/types.ts';

import { SKIP_VALUE } from './constants.ts';

export function avg(nums: number[]) {
  if (!nums.length) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/** Substanzielle Antwort: weder unbeantwortet noch „Keine Angabe" (SKIP_VALUE).
 * Gemeinsame Quelle für Score-Berechnung, Web- und PDF-Darstellung. */
export function isAnswered(value: number | undefined): value is number {
  return value !== undefined && value !== SKIP_VALUE;
}

/** Mittelwert der substanziell beantworteten Fragen; `undefined`, wenn keine
 * vorliegen — „keine Angabe" statt eines unmöglichen Skalenwerts 0. */
export function avgAnswered(values: (number | undefined)[]): number | undefined {
  const answered = values.filter(isAnswered);
  return answered.length ? avg(answered) : undefined;
}

/** Skala 1–5 → Prozent (1 = linker Anschlag 0 %, 5 = rechter Anschlag 100 %).
 * Auf 2 Nachkommastellen gerundet, damit Fließkomma-Rauschen (z. B.
 * 55.00000001 %) nicht im style-Attribut landet. Gemeinsame Quelle für alle
 * linearen Balken in Web und PDF. Score-Ringe/-Dials kodieren dagegen bewusst
 * den Anteil am Maximum (value/5) — sie sind Tachos, keine verankerten Skalen. */
export function scalePct(value: number) {
  return Math.round(Math.max(0, Math.min(1, (value - 1) / 4)) * 10000) / 100;
}

export function subgroupScores(section: Section, answers: Answers) {
  return section.subgroups.map((g) => ({
    id: g.id,
    label: g.title ?? '',
    score: avgAnswered(g.questions.map((q) => answers[q.id]))
  }));
}

export function getResultType(score: number): ResultType {
  return RESULT_TYPES.find((r) => score >= r.min && score <= r.max + 0.0001) ?? RESULT_TYPES[0];
}

/** Schneidet auf eine Nachkommastelle ab (kein Runden), damit die einstellige
 * Anzeige (z.B. "3,0") nie eine Stufen-Grenze zeigt, die der exakte Score noch
 * nicht erreicht hat — Anzeige und getResultType() müssen denselben Wert sehen. */
export function truncateToDisplay(score: number): number {
  return Math.floor(score * 10) / 10;
}

export const toneStyles: Record<
  ResultType['tone'],
  { bg: string; fg: string; dot: string; soft: string }
> = {
  red: {
    bg: 'bg-zeb-tone-red-surface',
    fg: 'text-zeb-tone-red',
    dot: 'bg-zeb-tone-red',
    soft: 'bg-zeb-tone-red'
  },
  darkorange: {
    bg: 'bg-zeb-tone-darkorange-surface',
    fg: 'text-zeb-tone-darkorange',
    dot: 'bg-zeb-tone-darkorange',
    soft: 'bg-zeb-tone-darkorange'
  },
  orange: {
    bg: 'bg-zeb-tone-orange-surface',
    fg: 'text-zeb-tone-orange',
    dot: 'bg-zeb-tone-orange',
    soft: 'bg-zeb-tone-orange'
  },
  lime: {
    bg: 'bg-zeb-tone-lime-surface',
    fg: 'text-zeb-tone-lime',
    dot: 'bg-zeb-tone-lime',
    soft: 'bg-zeb-tone-lime'
  },
  green: {
    bg: 'bg-zeb-tone-green-surface',
    fg: 'text-zeb-tone-green',
    dot: 'bg-zeb-tone-green',
    soft: 'bg-zeb-tone-green'
  }
};

export function shortSectionTitle(s: Section) {
  if (s.id === 'strategie') return 'Strategie zur Kundenzentrierung';
  if (s.id === 'loop') return 'Loop der Kundenzentrierung';
  return 'Wirkungsebenen der Kundenzentrierung';
}

export function subgroupLabel(sec: Section, subIdx: number) {
  const visible = sec.subgroups.filter((g) => g.title);
  if (!visible.length) return '';
  const cur = sec.subgroups[subIdx];
  if (!cur.title) return '';
  const pos = visible.findIndex((g) => g.id === cur.id) + 1;
  return `Abschnitt ${pos} von ${visible.length}`;
}
